import React from 'react';
import { ApolloError, useQuery } from '@apollo/client';

import CATALOG_COURSE_DURATION from '../../graphql/CatalogCourseDuration.graphql';
import { useAppContext } from '../../context/AppContext';

import type { CatalogCourseDuration, DurationItemExtended } from './types';

interface HookResponse {
  data: DurationItemExtended[];
  loading: boolean;
  error?: ApolloError;
  totalCount: number;
  hasSelectedItems: boolean;
  selectDuration: (duration: DurationItemExtended) => void;
  getDurationByTag: (tag: string) => DurationItemExtended | undefined;
}

const useCatalogCourseDuration = (): HookResponse => {
  const {
    filterActions,
    filters: {
      duration: { selectedTags },
    },
  } = useAppContext();
  const [durationItems, setDurationItems] = React.useState<
    DurationItemExtended[]
  >([]);
  const { data, loading, error } = useQuery<CatalogCourseDuration>(
    CATALOG_COURSE_DURATION,
    {
      fetchPolicy: 'cache-and-network',
      context: { clientName: 'v2' },
    },
  );

  React.useEffect(() => {
    if (data && data.catalogCourseDuration) {
      const items = (data?.catalogCourseDuration || []).map(
        item =>
          ({
            ...item,
            disabled: false,
            selected: selectedTags.includes(item!.tag!),
          } as DurationItemExtended),
      );

      setDurationItems(items);
    }
  }, [data, selectedTags, selectedTags.length]);

  const hasSelectedItems = React.useMemo(
    () => durationItems.some(item => item.selected),
    [durationItems],
  );
  const totalCount = durationItems.length || 0;

  const selectDuration = (duration: DurationItemExtended) => {
    const newDurationItems = durationItems.map(item => {
      if (item.tag === duration.tag) {
        return { ...item, selected: !item.selected };
      }

      return item;
    });

    setDurationItems(newDurationItems);
    filterActions.toggleFilterDuration(duration.tag!);
  };

  const getDurationByTag = React.useCallback(
    (tag: string) => {
      return durationItems.find(item => item.tag === tag);
    },
    [durationItems],
  );

  return {
    data: durationItems,
    loading,
    error,
    totalCount,
    hasSelectedItems,
    selectDuration,
    getDurationByTag,
  };
};

export default useCatalogCourseDuration;
